import { useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { AuthShell } from "@/components/customer/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ensureCustomerProfile } from "@/lib/customer/customer-api";
import { EmailVerificationNotice } from "@/components/customer/EmailVerificationNotice";
import { verificationRedirect } from "@/lib/customer/email-verification";

export const Route = createFileRoute("/account/sign-up")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Create your account — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content: "Create an account to save your delivery addresses and profile details.",
      },
      { property: "og:title", content: "Create your account — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content: "Create an account to save your delivery addresses and profile details.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SignUpPage,
});

function SignUpPage() {
  const navigate = useNavigate();
  const rawRedirect = (Route.useSearch() as { redirect?: string }).redirect;
  // Same-origin paths only, to avoid redirecting off-site.
  const redirect =
    rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
      ? rawRedirect
      : undefined;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current || verificationEmail) return;
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: verificationRedirect(window.location.origin, redirect),
        data: { full_name: fullName },
      },
    });
    if (signUpError) {
      setError(signUpError.message);
      return;
    }
    if (data.session) {
      await ensureCustomerProfile().catch(() => null);
      if (redirect) window.location.href = redirect;
      else navigate({ to: "/account", replace: true });
      return;
    }
    setPassword("");
    setVerificationEmail(email.trim());
    } catch {
      setError("We couldn’t complete registration. Please try again later.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Create your account"
      description="Save your details once and reuse them next time."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/account/sign-in" search={(redirect ? { redirect } : {}) as never} className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {verificationEmail ? <EmailVerificationNotice email={verificationEmail} redirect={redirect} requested
        onBack={() => navigate({ to: "/account/sign-in", search: (redirect ? { redirect } : {}) as never })} /> :
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor="signup-name">Full name</Label>
          <Input
            id="signup-name"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>}
    </AuthShell>
  );
}
